// The single place the app talks HTTP.
//
// The API is same-origin: Vite proxies /api in development and the server
// serves the built SPA in production, so there is no configurable base URL and
// no CORS anywhere. The bearer token is supplied by the auth layer through
// `setAccessTokenProvider`, so feature services never touch MSAL.
import { ApiRequestError } from './errors.ts'

export type AccessTokenProvider = () => Promise<string | null>

let provider: AccessTokenProvider = async () => null

export const setAccessTokenProvider = (next: AccessTokenProvider): void => { provider = next }

/**
 * The current token, for the one caller that cannot go through `apiRequest`:
 * asset transfers carry a raw body and their own timeout. Everything else
 * should use `apiRequest` and never touch this.
 */
export const accessToken = (): Promise<string | null> => provider()

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /**
   * Override the default budget. Only for calls that are legitimately slow --
   * an AI design turn is a model inference, not a database read -- and never as
   * a way to paper over a slow endpoint.
   */
  timeoutMs?: number
}

const TIMEOUT_MS = 20_000
/** Nothing should hold a request open longer than this, whatever it asks for. */
const MAX_TIMEOUT_MS = 180_000

/**
 * Exported so the routes that carry raw bytes -- which cannot go through
 * `apiRequest` -- report a failure the same way everything else does. A
 * hand-written message there would hide the server's own, which is the only
 * one that says what actually went wrong.
 */
export async function parseError(response: Response): Promise<ApiRequestError> {
  const fallback = `${response.status} ${response.statusText}`.trim()
  try {
    const payload = await response.json() as {
      error?: { code?: string; message?: string; details?: Record<string, unknown> }
    }
    const error = payload?.error
    return new ApiRequestError(
      response.status,
      error?.code ?? 'request_failed',
      error?.message ?? fallback,
      error?.details,
    )
  } catch {
    return new ApiRequestError(response.status, 'request_failed', fallback)
  }
}

async function tokenFor(signal: AbortSignal): Promise<string | null> {
  signal.throwIfAborted()
  let abort: () => void = () => {}
  const cancelled = new Promise<never>((_resolve, reject) => {
    abort = () => reject(new DOMException('Request cancelled.', 'AbortError'))
    signal.addEventListener('abort', abort, { once: true })
  })
  try {
    return await Promise.race([Promise.resolve().then(provider), cancelled])
  } finally {
    signal.removeEventListener('abort', abort)
  }
}

async function request<T>(
  path: string, options: RequestOptions, read: (response: Response) => Promise<T>,
): Promise<T> {
  const controller = new AbortController()
  const budget = Math.min(options.timeoutMs ?? TIMEOUT_MS, MAX_TIMEOUT_MS)
  const timeout = setTimeout(() => controller.abort(), budget)
  const abort = () => controller.abort()
  if (options.signal?.aborted) abort()
  options.signal?.addEventListener('abort', abort, { once: true })

  try {
    const headers: Record<string, string> = {}
    const token = await tokenFor(controller.signal)
    if (token) headers.authorization = `Bearer ${token}`
    if (options.body !== undefined) headers['content-type'] = 'application/json'
    controller.signal.throwIfAborted()
    const response = await fetch(`/api${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
      credentials: 'same-origin',
    })

    if (!response.ok) throw await parseError(response)
    return await read(response)
  } catch (error) {
    if (error instanceof ApiRequestError) throw error
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ApiRequestError(0, 'timeout', 'The request timed out.')
    }
    throw new ApiRequestError(0, 'network_error', 'Could not reach the ShapePilot API.')
  } finally {
    clearTimeout(timeout)
    options.signal?.removeEventListener('abort', abort)
  }
}

/** Every request is bounded, cancellable, and carries a fresh access token. */
export function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return request(path, options, async response => {
    if (response.status === 204) return null as T
    const text = await response.text()
    return (text ? JSON.parse(text) : null) as T
  })
}

/** Authenticated reports are downloaded, never put behind an anonymous URL. */
export function apiBlobRequest(path: string, options: RequestOptions = {}): Promise<Blob> {
  return request(path, options, response => response.blob())
}
